import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PartsInventoryPanel = ({ partsInventory, workOrders, onRefresh }) => {
  const [stockFilter, setStockFilter] = useState('all');

  const filteredParts = stockFilter === 'all'
    ? partsInventory
    : partsInventory?.filter(part => part?.status === stockFilter);

  const getStockBadge = (status) => {
    const config = {
      'in-stock': { label: 'In Stock', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
      'low-stock': { label: 'Low Stock', color: 'text-warning', bg: 'bg-warning/10', icon: 'AlertTriangle' },
      'out-of-stock': { label: 'Out of Stock', color: 'text-error', bg: 'bg-error/10', icon: 'XCircle' }
    };
    const cfg = config?.[status] || config?.['in-stock'];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        <Icon name={cfg?.icon} size={12} />
        {cfg?.label}
      </span>
    );
  };

  const stats = {
    total: partsInventory?.length || 0,
    inStock: partsInventory?.filter(p => p?.status === 'in-stock')?.length || 0,
    lowStock: partsInventory?.filter(p => p?.status === 'low-stock')?.length || 0,
    outOfStock: partsInventory?.filter(p => p?.status === 'out-of-stock')?.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Package" size={20} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">{stats?.total}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Total Parts</p>
        </div>

        <div className="bg-card rounded-xl border border-success/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircle2" size={20} className="text-success" />
            <span className="text-2xl font-bold text-success">{stats?.inStock}</span>
          </div>
          <p className="text-sm font-medium text-foreground">In Stock</p>
        </div>

        <div className="bg-card rounded-xl border border-warning/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <span className="text-2xl font-bold text-warning">{stats?.lowStock}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Low Stock</p>
        </div>

        <div className="bg-card rounded-xl border border-error/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="XCircle" size={20} className="text-error" />
            <span className="text-2xl font-bold text-error">{stats?.outOfStock}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Out of Stock</p>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStockFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              stockFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            All Parts
          </button>
          <button
            onClick={() => setStockFilter('in-stock')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              stockFilter === 'in-stock' ? 'bg-success text-success-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            In Stock
          </button>
          <button
            onClick={() => setStockFilter('low-stock')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              stockFilter === 'low-stock' ? 'bg-warning text-warning-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            Low Stock
          </button>
          <button
            onClick={() => setStockFilter('out-of-stock')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              stockFilter === 'out-of-stock' ? 'bg-error text-error-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            Out of Stock
          </button>
        </div>
      </div>

      {/* Parts List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredParts?.map((part) => (
          <div
            key={part?.id}
            className="bg-card rounded-xl border border-border p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">{part?.name}</h3>
                <p className="text-xs text-muted-foreground">{part?.category}</p>
              </div>
              {getStockBadge(part?.status)}
            </div>

            {/* Stock Level */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Stock Level</span>
                <span className="text-sm font-semibold text-foreground">
                  {part?.stock} / {part?.minStock}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    part?.status === 'out-of-stock' ? 'bg-error' :
                    part?.status === 'low-stock' ? 'bg-warning' : 'bg-success'
                  }`}
                  style={{ width: `${Math.min((part?.stock / part?.minStock) * 100, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              {part?.status === 'out-of-stock' && (
                <Button
                  variant="danger"
                  size="sm"
                  fullWidth
                  iconName="ShoppingCart"
                  iconPosition="left"
                >
                  Order Now
                </Button>
              )}
              {part?.status === 'low-stock' && (
                <Button
                  variant="warning"
                  size="sm"
                  fullWidth
                  iconName="ShoppingCart"
                  iconPosition="left"
                >
                  Reorder
                </Button>
              )}
              {part?.status === 'in-stock' && (
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="Eye"
                  iconPosition="left"
                >
                  View Details
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredParts?.length === 0 && (
        <div className="bg-card rounded-xl p-12 border border-border text-center">
          <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Parts Found</h3>
          <p className="text-muted-foreground">No parts match the selected filter</p>
        </div>
      )}
    </div>
  );
};

export default PartsInventoryPanel;